import { useState, useEffect, useRef } from 'react';
import { apiFetch } from './config';
import { relationColor } from './trustShared';

// The relation drawing FIELD (owner ruling 2026-08-04, records/
// RELATION_DRAWING_PREP.md R4: "the typing option is far and away the best").
//
// You TYPE the relation you mean between two objects already on screen; the
// matcher (GET /api/relations/match) resolves it and — when you phrase it
// backwards ("restated by", "is refuted by") — swaps the two endpoints
// VISIBLY, so direction is never a separate control. A grid of sentence
// buttons was designed first and rejected on density: a fixed button set
// cannot cover an open namespace of relation names.
//
// Match rows render in the compact connector form: endpoints named by
// POSITION (`aLabel`/`bLabel`, e.g. "that"/"this"), never by repeating their
// text — both objects are already visible, and the full sentence made every
// claim appear three times in one viewport (the EdgeSentence `connector`
// lesson, 2026-08-02).
//
// A relation drawn here is a HAND ASSERTION and carries the author's +1 — the
// same rule handleQuickLink and TrustStance already follow. NOT a loosening of
// activation: proposals (model output, retype, decompose) stay inert.
//
// An unmatched phrase offers to mint the relation type, preserving the open
// namespace. Refused synonyms never reach the create offer — the matcher
// already resolved them onto the canonical name.

export default function RelationField({
  a, // { id, type } — first endpoint in the display order
  b, // { id, type } — second endpoint (usually the current page's object)
  aLabel = 'that',
  bLabel = 'this',
  user,
  onDrawn,
  onCancel,
  autoFocus = false,
  placeholder = 'say the connection — “supports”, “is refuted by”, “answers”…',
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(autoFocus);
  const [candidates, setCandidates] = useState([]);
  const [create, setCreate] = useState(null);
  const [highlight, setHighlight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // late responses for a stale query must never override the current list —
  // a merge keyed by sequence, not a cancellation flag (the per-effect
  // cancellation lesson: cancelled work is silently dropped work)
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const mySeq = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (query.trim()) params.set('q', query.trim());
        if (a?.type) params.set('a_type', a.type);
        if (b?.type) params.set('b_type', b.type);
        const res = await apiFetch(`/api/relations/match?${params.toString()}`);
        if (!res.ok || mySeq !== seq.current) return;
        const json = await res.json();
        if (mySeq !== seq.current) return;
        setCandidates(json.candidates || []);
        setCreate(json.create || null);
        setHighlight(0);
      } catch {
        /* a failed match fetch leaves the previous list standing */
      }
    }, query.trim() ? 180 : 0);
    return () => clearTimeout(t);
  }, [query, open, a?.type, b?.type]);

  const options = create ? [...candidates, { create: true, name: create.name }] : candidates;

  const commit = async (opt) => {
    if (!user || busy || !opt) return;
    setBusy(true);
    setError(null);
    try {
      let relationId = opt.relation_id;
      if (opt.create) {
        const res = await apiFetch('/api/relations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: opt.name }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Could not create that relation type');
        relationId = json.id;
      }
      const sourceId = opt.source === 'b' ? b.id : a.id;
      const targetId = opt.source === 'b' ? a.id : b.id;
      const res = await apiFetch('/api/relation-instances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          relation_id: relationId,
          source_id: sourceId,
          target_id: targetId,
          polarity: 1,
        }),
      });
      const edge = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(edge.error || 'Could not draw the connection');
      // the hand-assertion +1 — an ordinary, withdrawable vote
      await apiFetch('/api/votes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relation_instance_id: edge.id, polarity: 1 }),
      });
      setQuery('');
      setOpen(false);
      if (onDrawn) onDrawn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(options[Math.min(highlight, options.length - 1)]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      if (onCancel) onCancel();
    }
  };

  if (!user) return null;

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 'min(240px, 100%)' }}>
      <input
        type="text"
        value={query}
        autoFocus={autoFocus}
        disabled={busy}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label="Relation between the two objects"
        style={{ width: '100%', padding: 'var(--wv-sp-2) var(--wv-sp-3)', boxSizing: 'border-box', fontSize: 'var(--wv-fs-ui)' }}
      />
      {open && options.length > 0 && (
        <div className="wv-dropdown" role="listbox">
          {options.map((opt, i) => {
            if (opt.create) {
              return (
                <div
                  key="__create"
                  role="option"
                  aria-selected={i === highlight}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => commit(opt)}
                  className={i === highlight ? 'wv-dropdown-item wv-tint' : 'wv-dropdown-item'}
                  style={{ fontStyle: 'italic' }}
                >
                  mint “{opt.name.replace(/_/g, ' ')}” — a new relation type
                </div>
              );
            }
            const first = opt.source === 'b' ? bLabel : aLabel;
            const second = opt.source === 'b' ? aLabel : bLabel;
            return (
              <div
                key={`${opt.relation_id}|${opt.source}`}
                role="option"
                aria-selected={i === highlight}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => commit(opt)}
                className={i === highlight ? 'wv-dropdown-item wv-tint' : 'wv-dropdown-item'}
                style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--wv-sp-2)', flexWrap: 'wrap' }}
              >
                <span>
                  <em>{first}</em>{' '}
                  <strong style={{ color: relationColor(opt.name) }}>
                    {opt.name.replace(/_/g, ' ')}
                  </strong>{' '}
                  <em>{second}</em>
                </span>
                {/* related_to is a DEFERRAL, not a reading (R6, ruled
                    2026-08-04): same row written — which is what queues the
                    pair — but the interface stops presenting it as saying
                    anything. Every surface with the field gets this at once. */}
                {opt.name === 'related_to' && (
                  <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                    defer — they connect, you can’t say how yet; joins the deferred queue
                  </span>
                )}
                {opt.matched && (
                  <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                    from “{opt.matched.replace(/_/g, ' ')}”
                  </span>
                )}
                {opt.fixed ? (
                  <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                    direction set by the types
                  </span>
                ) : opt.swapped ? (
                  <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                    ⇄ swapped
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {error && (
        <div style={{ color: 'var(--wv-neg)', fontSize: 'var(--wv-fs-sm)', marginTop: 'var(--wv-sp-1)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
